import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Button, Grid,
  Table, TableBody, TableRow, TableCell, ToggleButton, ToggleButtonGroup,
  alpha, useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  searchParliaments,
  searchBills,
  searchParliamentarians,
  searchHansard,
  searchDivisions,
  searchParties,
  searchCalendar
} from '../services/api';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

// The search APIs cap per_page at 100; requesting more returns HTTP 422.
const API_PER_PAGE_CAP = 100;
// Elasticsearch caps hits.total at 10000 unless track_total_hits is set, which
// these endpoints don't enable — so a total of exactly 10000 means "10000 or
// more". Display it honestly as "10k+" rather than rounding it to a fixed "10k".
const ES_TOTAL_CAP = 10000;

export default function ParliamentDetail() {
  const { pid } = useParams();
  const theme = useTheme();
  const [parliament, setParliament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Statistics
  const [stats, setStats] = useState({
    billsCount: null,
    parliamentariansCount: null,
    membersCount: null,
    senatorsCount: null,
    electoratesCount: null,
    sittingDays: null,
    divisionsCount: null,
    hansardCount: null,
    hansardCapped: false,
    partiesWithCounts: []
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [partyColors, setPartyColors] = useState({});
  // Party Composition user sort: 'count' (default, desc) or 'name' (A–Z).
  const [partySort, setPartySort] = useState('count');
  // Party Composition chamber filter (#247 sub-item 4): 'all' | 'member' |
  // 'senator'. When non-'all' we refetch with MPorSenator filter so the
  // server-side party facet returns chamber-scoped counts.
  const [chamberFilter, setChamberFilter] = useState('all');
  const [chamberPartiesLoading, setChamberPartiesLoading] = useState(false);
  const [chamberPartiesWithCounts, setChamberPartiesWithCounts] = useState(null);

  useEffect(() => {
    const fetchParliament = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await searchParliaments(`PID:${pid}`, 1, 1);
        if (data.hits && data.hits.length > 0) {
          setParliament(data.hits[0]);
        } else {
          setError('Parliament not found');
        }
      } catch (err) {
        setError('Failed to load parliament data');
      }
      setLoading(false);
    };
    fetchParliament();
  }, [pid]);

  // Fetch party colors. The parties API caps per_page at 100 (per_page>100 → 422,
  // the original 200 always failed — #164 finding 5), so page through in chunks
  // of 100 until we've collected them all.
  useEffect(() => {
    const fetchPartyColors = async () => {
      try {
        const colors = {};
        let page = 1;
        let total = Infinity;
        while ((page - 1) * API_PER_PAGE_CAP < total) {
          const data = await searchParties('*', page, API_PER_PAGE_CAP);
          total = data.total || 0;
          (data.hits || []).forEach((p) => {
            if (p.Colour) {
              colors[p.Name] = p.Colour;
              if (p.Abbrev) colors[p.Abbrev.trim()] = p.Colour;
            }
          });
          if (!data.hits || data.hits.length === 0) break;
          page += 1;
        }
        setPartyColors(colors);
      } catch (err) {
        // Colours are non-essential — getPartyColor() falls back to a heuristic.
      }
    };
    fetchPartyColors();
  }, []);

  // Fetch statistics
  useEffect(() => {
    if (!parliament) return;

    // Parliament date span — used by the date-ranged stats (hansard, divisions,
    // sitting days). The page already has these from /api/parliaments.
    const startDate = parliament.DateOpening
      ? parliament.DateOpening.split('T')[0]
      : null;
    const endDate = parliament.DateDissolution
      ? parliament.DateDissolution.split('T')[0]
      : new Date().toISOString().split('T')[0];

    const fetchStats = async () => {
      setStatsLoading(true);
      const newStats = {
        billsCount: null,
        parliamentariansCount: null,
        membersCount: null,
        senatorsCount: null,
        electoratesCount: null,
        sittingDays: null,
        divisionsCount: null,
        hansardCount: null,
        hansardCapped: false,
        partiesWithCounts: []
      };

      try {
        // Bills count for this parliament. The bills index field is
        // `parliament_number` (NOT `Parliament`) — #164 finding 3.
        const billsData = await searchBills(`parliament_number:${pid}`, 1, 1);
        newStats.billsCount = billsData.total || 0;
      } catch (err) {
        // leave billsCount null -> renders "—"
      }

      try {
        // Parliamentarians in this parliament. The parliamentarians index field
        // is `RepresentedParliaments` (an array of parliament numbers), NOT the
        // non-existent `ParliamentsServed` — #164 finding 1. Request a single
        // doc (per_page=1) and read `total`; the party breakdown comes from the
        // server-side `facets.parties` aggregation, so we never page the full
        // roster (which would 422 at per_page=1000 and under-count at <=100).
        const parlData = await searchParliamentarians(
          `RepresentedParliaments:${pid}`,
          1,
          1,
          'name_asc'
        );
        newStats.parliamentariansCount = parlData.total || 0;

        const partyFacets = (parlData.facets && parlData.facets.parties) || [];
        newStats.partiesWithCounts = partyFacets
          .filter((f) => f.value && f.count)
          .map((f) => ({ party: f.value, count: f.count }));

        // Chamber + electorate breakdowns (#247 sub-item 3). The server-side
        // aggs are `chambers` (terms on MPorSenator) + `electorates_count`
        // (cardinality on Electorate).
        const chamberFacets = (parlData.facets && parlData.facets.chambers) || [];
        const memberBucket = chamberFacets.find((f) => f.value === 'Member');
        const senatorBucket = chamberFacets.find((f) => f.value === 'Senator');
        newStats.membersCount = memberBucket ? memberBucket.count : 0;
        newStats.senatorsCount = senatorBucket ? senatorBucket.count : 0;
        const elecCount = parlData.facets && parlData.facets.electorates_count;
        newStats.electoratesCount = (typeof elecCount === 'number') ? elecCount : null;
      } catch (err) {
        // leave parliamentariansCount null -> renders "—"
      }

      try {
        // Divisions: the divisions index has NO parliament field (keys: date,
        // datetime, division_number, house, vote counts...), so derive the count
        // by date range over the `date` field within the parliament's span —
        // #164 finding 3. (Coverage is modern-only: pre-2025 ranges read 0.)
        if (startDate) {
          const divisionsData = await searchDivisions(
            `date:[${startDate} TO ${endDate}]`,
            1,
            1
          );
          newStats.divisionsCount = divisionsData.total || 0;
        }
      } catch (err) {
        // leave divisionsCount null -> renders "—"
      }

      try {
        // Hansard records for the parliament. The clean date-derived `parliament`
        // field (#158) only covers parliaments 39-48, whereas the lowercase
        // `date` field is populated across the whole corpus — so a date range
        // works for every parliament, current and historical (#164 finding 2,
        // and lets older parliaments like P20 render). The previous `Date:[...]`
        // used the wrong field case and always returned 0.
        if (startDate) {
          const hansardData = await searchHansard(
            `date:[${startDate} TO ${endDate}]`,
            1,
            1
          );
          newStats.hansardCount = hansardData.total || 0;
          newStats.hansardCapped = (hansardData.total || 0) >= ES_TOTAL_CAP;
        }
      } catch (err) {
        // leave hansardCount null -> renders "—"
      }

      try {
        // Sitting days: a real distinct-day count from the parliament_calendar
        // index (`is_sitting_day:true` within the date range), replacing the old
        // ~75-speeches-per-day heuristic that was a guess presented as fact.
        if (startDate) {
          const calData = await searchCalendar(
            `is_sitting_day:true AND date:[${startDate} TO ${endDate}]`,
            1,
            1
          );
          const sd = calData.total || 0;
          if (sd > 0) newStats.sittingDays = sd;
        }
      } catch (err) {
        // leave sittingDays null -> not surfaced
      }

      setStats(newStats);
      setStatsLoading(false);
    };

    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parliament, pid]);

  // Chamber-filtered party composition (#247 sub-item 4). Skips for 'all' (the
  // base parliamentarians fetch already returns the all-chambers party facet)
  // and refetches with an MPorSenator filter for 'member'/'senator'.
  useEffect(() => {
    if (!parliament) return;
    if (chamberFilter === 'all') {
      setChamberPartiesWithCounts(null);
      return;
    }
    const chamberValue = chamberFilter === 'member' ? 'Member' : 'Senator';
    const fetchChamberParties = async () => {
      setChamberPartiesLoading(true);
      try {
        const data = await searchParliamentarians(
          `RepresentedParliaments:${pid} AND MPorSenator:${chamberValue}`,
          1,
          1,
          'name_asc'
        );
        const partyFacets = (data.facets && data.facets.parties) || [];
        setChamberPartiesWithCounts(
          partyFacets
            .filter((f) => f.value && f.count)
            .map((f) => ({ party: f.value, count: f.count }))
        );
      } catch (err) {
        setChamberPartiesWithCounts([]);
      }
      setChamberPartiesLoading(false);
    };
    fetchChamberParties();
  }, [parliament, pid, chamberFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isCurrent = (p) => {
    return !p.DateDissolution || new Date(p.DateDissolution) > new Date();
  };

  const getOrdinalNumber = (n) => {
    if (n === 1) return '1st';
    if (n === 2) return '2nd';
    if (n === 3) return '3rd';
    if (n >= 11 && n <= 13) return `${n}th`;
    const lastDigit = n % 10;
    if (lastDigit === 1) return `${n}st`;
    if (lastDigit === 2) return `${n}nd`;
    if (lastDigit === 3) return `${n}rd`;
    return `${n}th`;
  };

  const getPartyColor = (partyName) => {
    // Try exact match first
    if (partyColors[partyName]) return partyColors[partyName];

    // Common party color fallbacks
    const lowerName = partyName.toLowerCase();
    if (lowerName.includes('labor') || lowerName.includes('labour')) return '#E53935';
    if (lowerName.includes('liberal') && !lowerName.includes('national')) return '#1565C0';
    if (lowerName.includes('national') || lowerName.includes('country')) return '#2E7D32';
    if (lowerName.includes('greens') || lowerName.includes('green')) return '#4CAF50';
    if (lowerName.includes('independent')) return '#757575';
    if (lowerName.includes('democrat')) return '#FF9800';

    return '#9E9E9E'; // Default grey
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !parliament) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Parliament not found'}</Typography>
        <Button component={RouterLink} to="/parliaments" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Parliaments
        </Button>
      </Box>
    );
  }

  const current = isCurrent(parliament);

  // Date span re-derived for building the date-ranged "View All" links so they
  // land on populated, correctly-filtered result pages (#164 finding 4).
  const linkStart = parliament.DateOpening ? parliament.DateOpening.split('T')[0] : null;
  const linkEnd = parliament.DateDissolution
    ? parliament.DateDissolution.split('T')[0]
    : new Date().toISOString().split('T')[0];

  // When the chamber filter is non-'all' AND we have a fetched chamber-scoped
  // breakdown, use it; otherwise fall back to the all-chambers party facet
  // baked into stats.partiesWithCounts.
  const basePartyCounts = (chamberFilter !== 'all' && chamberPartiesWithCounts != null)
    ? chamberPartiesWithCounts
    : stats.partiesWithCounts;
  const sortedParties = [...basePartyCounts].sort((a, b) => {
    if (partySort === 'name') return a.party.localeCompare(b.party);
    return b.count - a.count;
  });
  const maxPartyCount = sortedParties.length > 0
    ? Math.max(...sortedParties.map((p) => p.count))
    : 1;

  const hansardDisplay = stats.hansardCount == null
    ? '—'
    : stats.hansardCapped
      ? '10k+'
      : stats.hansardCount.toLocaleString();

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/parliaments"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Parliaments
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: current
            ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: current ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.primary.main, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <AccountBalanceIcon sx={{ fontSize: 60, color: current ? 'success.main' : 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {current && <Chip label="Current Parliament" color="success" />}
              {!current && <Chip label="Historical" variant="outlined" />}
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: current ? 'success.dark' : 'primary.main', mb: 1 }}>
              {getOrdinalNumber(parliament.PID)} Parliament
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {parliament.FQName || parliament.Name}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Key Dates */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon color="primary" />
              Key Dates
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Election Date</TableCell>
                  <TableCell>{formatDate(parliament.DateElection)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Parliament Opened</TableCell>
                  <TableCell>{formatDate(parliament.DateOpening)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Parliament Dissolved</TableCell>
                  <TableCell>
                    {current ? (
                      <Chip label="Currently in session" size="small" color="success" />
                    ) : (
                      formatDate(parliament.DateDissolution)
                    )}
                  </TableCell>
                </TableRow>
                {stats.sittingDays != null && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Sitting Days</TableCell>
                    <TableCell>{stats.sittingDays.toLocaleString()}</TableCell>
                  </TableRow>
                )}
                {parliament.ParliamentEnd && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Parliament End</TableCell>
                    <TableCell>{formatDate(parliament.ParliamentEnd)}</TableCell>
                  </TableRow>
                )}
                {/* #247 (2): per-session + prorogation rows from the Handbook
                    StatisticalInformation feed (#273). Sessions are ordered as
                    the upstream payload returns them (chronological). */}
                {(parliament.Sessions || []).map((sess, idx) => {
                  const sessionNum = idx + 1;
                  const opening = formatDate(sess.DateOpening) || '—';
                  const closing = sess.DateClosing
                    ? formatDate(sess.DateClosing)
                    : 'Current';
                  return (
                    <React.Fragment key={`session-${sessionNum}`}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          Session {sessionNum}
                        </TableCell>
                        <TableCell>
                          {opening} → {closing}
                        </TableCell>
                      </TableRow>
                      {(sess.Prorogations || []).map((p, pidx) => {
                        let label = 'Prorogued';
                        if (p.FurtherProrogation) label = 'Further prorogation';
                        else if (p.ByGovernorGeneral) label = 'Prorogued by Governor-General';
                        return (
                          <TableRow key={`session-${sessionNum}-prog-${pidx}`}>
                            <TableCell sx={{ pl: 4, fontWeight: 400, color: 'text.secondary', fontSize: '0.85rem' }}>
                              {label}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>
                              {formatDate(p.DateProrogued) || '—'}
                              {p.ProrogationProclamation && (
                                <Button
                                  href={p.ProrogationProclamation}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  sx={{ ml: 1, minWidth: 'auto', p: 0.25 }}
                                  title="View prorogation proclamation"
                                >
                                  <LaunchIcon sx={{ fontSize: 14 }} />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Parliament Statistics
            </Typography>
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}
                    title="Distinct people who sat as a Member of the House for this parliament (counts replacements/by-election winners separately, not seats)."
                  >
                    <GroupIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {stats.membersCount?.toLocaleString() ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Members served</Typography>
                    <Button
                      component={RouterLink}
                      to={`/parliamentarians?q=RepresentedParliaments:${pid} AND MPorSenator:Member`}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      View All
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2 }}
                    title="Distinct people who sat as a Senator during this parliament (counts replacements separately, not the fixed seat total)."
                  >
                    <GroupIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                      {stats.senatorsCount?.toLocaleString() ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Senators served</Typography>
                    <Button
                      component={RouterLink}
                      to={`/parliamentarians?q=RepresentedParliaments:${pid} AND MPorSenator:Senator`}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      View All
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box
                    sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}
                    title="Distinct electorates represented by at least one Member during this parliament."
                  >
                    <GroupIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      {stats.electoratesCount?.toLocaleString() ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Electorates represented</Typography>
                    <Button
                      component={RouterLink}
                      to={`/electorates`}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      View All
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                      {stats.billsCount?.toLocaleString() ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Bills</Typography>
                    <Button
                      component={RouterLink}
                      to={`/bills?q=parliament_number:${pid}`}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      View All
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 2 }}>
                    <HowToVoteIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.dark' }}>
                      {stats.divisionsCount?.toLocaleString() ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Divisions</Typography>
                    {linkStart && (
                      <Button
                        component={RouterLink}
                        to={`/divisions?q=date:[${linkStart} TO ${linkEnd}]`}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        View All
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                    <RecordVoiceOverIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.dark' }}>
                      {hansardDisplay}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Hansard Records</Typography>
                    {linkStart && (
                      <Button
                        component={RouterLink}
                        to={`/hansard?q=date:[${linkStart} TO ${linkEnd}]`}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        View All
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Party Composition */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <GavelIcon color="primary" />
                Party Composition ({sortedParties.length} {sortedParties.length === 1 ? 'party' : 'parties'})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={chamberFilter}
                  onChange={(e, val) => { if (val) setChamberFilter(val); }}
                  aria-label="Filter by chamber"
                >
                  <ToggleButton value="all" aria-label="Both chambers">All</ToggleButton>
                  <ToggleButton value="member" aria-label="Members only">Members</ToggleButton>
                  <ToggleButton value="senator" aria-label="Senators only">Senators</ToggleButton>
                </ToggleButtonGroup>
                {sortedParties.length > 1 && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={partySort}
                    onChange={(e, val) => { if (val) setPartySort(val); }}
                    aria-label="Sort party composition"
                  >
                    <ToggleButton value="count" aria-label="Sort by member count">Count</ToggleButton>
                    <ToggleButton value="name" aria-label="Sort alphabetically">A–Z</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
            </Box>
            {(statsLoading || chamberPartiesLoading) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : sortedParties.length > 0 ? (
              <Box>
                {sortedParties.map(({ party, count }) => {
                  const color = getPartyColor(party);
                  const percentage = (count / maxPartyCount) * 100;
                  const chamberClause = chamberFilter === 'member'
                    ? ' AND MPorSenator:Member'
                    : chamberFilter === 'senator'
                      ? ' AND MPorSenator:Senator'
                      : '';
                  const personLabel = chamberFilter === 'member'
                    ? 'member'
                    : chamberFilter === 'senator'
                      ? 'senator'
                      : 'parliamentarian';
                  return (
                    <Box key={party} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Button
                          component={RouterLink}
                          to={`/parliamentarians?q=RepresentedParliaments:${pid} AND Party:"${party}"${chamberClause}`}
                          sx={{
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                            color: 'text.primary',
                            '&:hover': { color: color }
                          }}
                        >
                          {party}
                        </Button>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {count} {personLabel}{count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'relative', height: 24, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${percentage}%`,
                            bgcolor: color,
                            borderRadius: 1,
                            transition: 'width 0.5s ease'
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary">No party data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Parties in Parliament (from parliament data) */}
        {parliament.PartiesByParliament && parliament.PartiesByParliament.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Registered Parties ({parliament.PartiesByParliament.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {parliament.PartiesByParliament.map((party, idx) => (
                  <Chip
                    key={idx}
                    label={party.Abbrev?.trim() || party.Name}
                    size="small"
                    sx={{
                      bgcolor: party.Colour || getPartyColor(party.Name),
                      color: 'white',
                      fontWeight: 500
                    }}
                    title={party.Name}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
